import React, { useState, useEffect, useCallback, useRef } from "react";
import { AdminApis } from "../../apis/adminApi";
import {
  FaTrash,
  FaEdit,
  FaSearch,
  FaPlus,
  FaExternalLinkAlt,
  FaStore,
  FaTh,
  FaList,
  FaCopy,
  FaTag,
  FaMapMarkerAlt,
  FaChevronRight,
} from "react-icons/fa";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { ToastContainer, toast } from "react-toastify";
import { NavLink } from "react-router-dom";
import Modal from "react-awesome-modal";
import { useSelector } from "react-redux";
import { SvgElement, icontypesEnum } from "../assets/svgElement";
import { Oval } from "react-loader-spinner";
import CardRenewSubscription from "./CardRenewSubscription";
import configs from "../../configs";

export default function CardMiniStore() {
  const userLoginData = useSelector((state) => state.data.login.value);

  const [modals, setModals] = useState({ edit: false, delete: false, exceed: false });
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [viewMode, setViewMode] = useState("grid");
  const [sortBy, setSortBy] = useState("newest");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedMarketLink, setSelectedMarketLink] = useState("all");
  const [message, setMessage] = useState("");
  const [, setCurrentPage] = useState(1);
  const [paginationData, setPaginationData] = useState(null);
  const searchInputRef = useRef(null);

  const categories = [
    { value: "all", label: "All" },
    { value: "women fashion", label: "Women's" },
    { value: "men fashion", label: "Men's" },
    { value: "beauty&cosmetics", label: "Beauty" },
    { value: "bags", label: "Bags" },
    { value: "sport/outdoor", label: "Sport" },
    { value: "home/kitchen", label: "Home" },
    { value: "shoes", label: "Shoes" },
    { value: "watches", label: "Watches" },
    { value: "laptops", label: "Laptops" },
    { value: "phones", label: "Phones" },
  ];

  const fetchProducts = useCallback(async (page = "") => {
    try {
      setLoading(true);
      const response = await AdminApis.getAllStore(page);
      if (response?.data) {
        setProducts(response.data?.data?.data || []);
        setPaginationData(response.data?.data);
        setLoading(false);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
      setLoading(false);
      toast.error("Failed to load products");
    }
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  useEffect(() => {
    let filtered = [...products];
    if (searchTerm.trim()) {
      filtered = filtered.filter(
        (p) =>
          p.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          p.product_description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          p.link_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (selectedCategory !== "all") {
      filtered = filtered.filter((p) => p.category === selectedCategory);
    }
    if (selectedMarketLink !== "all") {
      filtered = filtered.filter((p) => p.link_name === selectedMarketLink);
    }
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "newest": return new Date(b.created_at) - new Date(a.created_at);
        case "oldest": return new Date(a.created_at) - new Date(b.created_at);
        case "price-low": return parseFloat(a.product_price || 0) - parseFloat(b.product_price || 0);
        case "price-high": return parseFloat(b.product_price || 0) - parseFloat(a.product_price || 0);
        case "name": return (a.product_name || "").localeCompare(b.product_name || "");
        default: return 0;
      }
    });
    setFilteredProducts(filtered);
  }, [products, searchTerm, selectedCategory, selectedMarketLink, sortBy]);

  const getUniqueMarketLinks = () =>
    [...new Set(products.map((p) => p.link_name).filter(Boolean))];

  const toggleModal = (modalName, state = null, product = null) => {
    setModals((prev) => ({ ...prev, [modalName]: state !== null ? state : !prev[modalName] }));
    if (product) setSelectedProduct(product);
  };

  const updateProduct = async (e) => {
    e.preventDefault();
    try {
      const data = {
        message,
        id: selectedProduct?.id,
        name: selectedProduct?.product_name,
        phone_number: selectedProduct?.link_info?.phone_number,
      };
      const response = await AdminApis.editLink(data);
      if (response?.data) {
        toast.success(response.data.message);
        toggleModal("edit", false);
        fetchProducts();
        setMessage("");
      }
    } catch (error) {
      toast.error("Failed to update product");
    }
  };

  const deleteProduct = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await AdminApis.deleteProduct(selectedProduct);
      if (response?.data) {
        toast.success("Product deleted successfully");
        toggleModal("delete", false);
        fetchProducts();
      }
    } catch (error) {
      toast.error("Failed to delete product");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    fetchProducts(page);
    setCurrentPage(page);
  };

  const truncateText = (str, length = 60) => {
    if (!str) return "";
    return str.length > length ? str.substring(0, length) + "..." : str;
  };

  const formatPrice = (price) =>
    new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN" }).format(price || 0);

  const copyToClipboard = () =>
    toast.success("Link copied!", { position: "bottom-right", autoClose: 2000 });

  /* ─── Product Card ─────────────────────────────────────────────────────── */
  const ProductCard = ({ product, isListView = false }) => (
    <div
      className={`group bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 overflow-hidden ${
        isListView ? "flex items-center" : "flex flex-col"
      }`}
    >
      {/* Image */}
      <div
        className={`relative overflow-hidden bg-gray-50 flex-shrink-0 ${
          isListView ? "w-24 h-24 rounded-l-2xl" : "aspect-square w-full"
        }`}
      >
        {product.product_image_1 ? (
          <img
            src={product.product_image_1}
            alt={product.product_name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
            <FaStore className="text-gray-300" size={isListView ? 20 : 36} />
          </div>
        )}

        {/* Store badge */}
        {!isListView && (
          <div className="absolute top-2 left-2">
            <span className="bg-white/90 backdrop-blur-sm text-gray-600 text-[10px] font-semibold px-2 py-0.5 rounded-full border border-gray-200 shadow-sm">
              {truncateText(product.link_name, 10)}
            </span>
          </div>
        )}

        {/* Hover quick-actions (grid only) */}
        {!isListView && (
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-end justify-center pb-4 gap-2">
            <NavLink
              to={`/edit-product/${product.id}`}
              className="flex items-center gap-1 px-3 py-1.5 bg-white text-gray-800 text-xs font-semibold rounded-full shadow hover:bg-gray-100 transition-colors"
            >
              <FaEdit size={10} /> Edit
            </NavLink>
            <a
              href={`/s/${product.link_name}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 px-3 py-1.5 bg-white text-gray-800 text-xs font-semibold rounded-full shadow hover:bg-gray-100 transition-colors"
            >
              <FaExternalLinkAlt size={10} /> View
            </a>
          </div>
        )}
      </div>

      {/* Info */}
      <div className={`flex flex-col flex-1 ${isListView ? "px-4 py-3" : "p-4"}`}>
        {/* Name */}
        <h3
          className={`font-semibold text-gray-900 leading-snug mb-1 ${
            isListView ? "text-sm" : "text-sm"
          } line-clamp-2`}
        >
          {product.product_name}
        </h3>

        {/* Category + Location (grid only) */}
        {!isListView && (product.category || product.location) && (
          <div className="flex flex-wrap gap-1 mb-2">
            {product.category && (
              <span className="inline-flex items-center gap-0.5 bg-blue-50 text-blue-600 text-[10px] font-medium px-1.5 py-0.5 rounded-full">
                <FaTag size={7} />
                {truncateText(product.category, 16)}
              </span>
            )}
            {product.location && (
              <span className="inline-flex items-center gap-0.5 bg-emerald-50 text-emerald-600 text-[10px] font-medium px-1.5 py-0.5 rounded-full">
                <FaMapMarkerAlt size={7} />
                {truncateText(product.location, 12)}
              </span>
            )}
          </div>
        )}

        {/* Price */}
        <div className="flex items-baseline gap-2 mb-3">
          <span className="text-base font-bold text-gray-900">
            {formatPrice(product.product_price)}
          </span>
          {product.no_of_items &&
            parseFloat(product.no_of_items) < parseFloat(product.product_price) && (
              <span className="text-xs text-gray-400 line-through">
                {formatPrice(product.no_of_items)}
              </span>
            )}
        </div>

        {/* Actions */}
        <div className={`flex items-center ${isListView ? "gap-1" : "gap-1.5 border-t border-gray-100 pt-3"}`}>
          <CopyToClipboard
            text={`${configs?.baseRedirectFront}s/${product.link_name}`}
            onCopy={copyToClipboard}
          >
            <button
              title="Copy store link"
              className="flex-1 flex items-center justify-center gap-1 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
            >
              <FaCopy size={10} />
              {!isListView && "Copy"}
            </button>
          </CopyToClipboard>

          <NavLink
            to={`/edit-product/${product.id}`}
            title="Edit"
            className="flex items-center justify-center w-8 h-8 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
          >
            <FaEdit size={12} />
          </NavLink>

          <a
            href={`${configs?.baseRedirectFront}s/${product.link_name}`}
            target="_blank"
            rel="noopener noreferrer"
            title="View store"
            className="flex items-center justify-center w-8 h-8 text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
          >
            <FaExternalLinkAlt size={11} />
          </a>

          <button
            onClick={() => toggleModal("delete", true, product.id)}
            title="Delete"
            className="flex items-center justify-center w-8 h-8 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            <FaTrash size={11} />
          </button>
        </div>
      </div>
    </div>
  );

  /* ─── Empty State ─────────────────────────────────────────────────────── */
  const EmptyState = () => (
    <div className="col-span-full flex flex-col items-center justify-center py-24 px-4 text-center">
      <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-3xl flex items-center justify-center mb-6 shadow-inner">
        <FaStore className="text-blue-400" size={30} />
      </div>
      <h3 className="text-xl font-bold text-gray-900 mb-2">
        {searchTerm || selectedCategory !== "all" || selectedMarketLink !== "all"
          ? "No products match your filters"
          : "Your store is empty"}
      </h3>
      <p className="text-gray-500 mb-8 max-w-sm text-sm leading-relaxed">
        {searchTerm || selectedCategory !== "all" || selectedMarketLink !== "all"
          ? "Try adjusting your search or filters."
          : "Add your first product and start selling to customers across Nigeria."}
      </p>
      {!searchTerm && selectedCategory === "all" && selectedMarketLink === "all" && (
        <NavLink
          to="/createproduct"
          className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 transition-colors shadow-md hover:shadow-lg"
        >
          <FaPlus size={12} />
          Add First Product
        </NavLink>
      )}
    </div>
  );

  /* ─── Loading ─────────────────────────────────────────────────────────── */
  if (loading && products.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Oval height={40} width={40} color="#3b82f6" secondaryColor="#bfdbfe" strokeWidth={3} />
          <p className="mt-4 text-sm text-gray-500 font-medium">Loading products…</p>
        </div>
      </div>
    );
  }

  if (paginationData?.data === "sub_expired") return <CardRenewSubscription />;

  /* ─── Main Render ─────────────────────────────────────────────────────── */
  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">

        {/* ── Page Header ─────────────────────────────────────────────── */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">My Products</h1>
            <p className="mt-0.5 text-sm text-gray-500">
              {products.length} {products.length === 1 ? "product" : "products"} across{" "}
              {getUniqueMarketLinks().length}{" "}
              {getUniqueMarketLinks().length === 1 ? "store" : "stores"}
            </p>
          </div>

          <NavLink
            to={
              userLoginData?.data?.no_of_mstore <= products.length
                ? "#"
                : "/createproduct"
            }
            onClick={
              userLoginData?.data?.no_of_mstore <= products.length
                ? () => toggleModal("exceed", true)
                : undefined
            }
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 transition-colors shadow-md hover:shadow-lg whitespace-nowrap"
          >
            <FaPlus size={12} />
            Add Product
          </NavLink>
        </div>

        {/* ── Category Pills ───────────────────────────────────────────── */}
        <div className="flex gap-2 overflow-x-auto pb-1 no-scrollbar">
          {categories.map((cat) => (
            <button
              key={cat.value}
              onClick={() => setSelectedCategory(cat.value)}
              className={`flex-shrink-0 px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 border ${
                selectedCategory === cat.value
                  ? "bg-blue-600 text-white border-blue-600 shadow-md"
                  : "bg-white text-gray-600 border-gray-200 hover:border-blue-300 hover:text-blue-600"
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* ── Filter & Controls Bar ────────────────────────────────────── */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
          <div className="flex flex-wrap gap-3 items-center">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <FaSearch
                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
                size={13}
              />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search products…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
            </div>

            {/* Market link filter */}
            <select
              value={selectedMarketLink}
              onChange={(e) => setSelectedMarketLink(e.target.value)}
              className="px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Stores</option>
              {getUniqueMarketLinks().map((link) => (
                <option key={link} value={link}>
                  {link}
                </option>
              ))}
            </select>

            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="newest">Newest</option>
              <option value="oldest">Oldest</option>
              <option value="name">Name A–Z</option>
              <option value="price-low">Price ↑</option>
              <option value="price-high">Price ↓</option>
            </select>

            {/* View toggle */}
            <div className="flex bg-gray-100 rounded-xl p-1 gap-0.5">
              <button
                onClick={() => setViewMode("grid")}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-all duration-200 ${
                  viewMode === "grid"
                    ? "bg-white text-blue-600 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <FaTh size={11} /> Grid
              </button>
              <button
                onClick={() => setViewMode("list")}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-all duration-200 ${
                  viewMode === "list"
                    ? "bg-white text-blue-600 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <FaList size={11} /> List
              </button>
            </div>

            {/* Result count */}
            {(searchTerm || selectedCategory !== "all" || selectedMarketLink !== "all") && (
              <span className="text-xs text-gray-500 font-medium">
                {filteredProducts.length} result{filteredProducts.length !== 1 ? "s" : ""}
              </span>
            )}
          </div>
        </div>

        {/* ── Products Grid / List ─────────────────────────────────────── */}
        {filteredProducts.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
            <EmptyState />
          </div>
        ) : (
          <div
            className={
              viewMode === "grid"
                ? "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-5"
                : "flex flex-col gap-3"
            }
          >
            {filteredProducts.map((product) => (
              <ProductCard key={product.id} product={product} isListView={viewMode === "list"} />
            ))}
          </div>
        )}

        {/* ── Pagination ───────────────────────────────────────────────── */}
        {paginationData?.links && paginationData.links.length > 3 && (
          <div className="flex justify-center mt-4">
            <div className="inline-flex items-center gap-1 bg-white border border-gray-200 rounded-2xl p-1.5 shadow-sm">
              {paginationData.links.map((link, index) => (
                <button
                  key={index}
                  onClick={() => {
                    if (
                      link.label === "Next &raquo;" ||
                      link.label === "&laquo; Previous"
                    ) {
                      handlePageChange(link.url?.charAt(link.url.length - 1));
                    } else if (!isNaN(link.label)) {
                      handlePageChange(link.label);
                    }
                  }}
                  disabled={link.active || !link.url}
                  className={`min-w-[36px] h-9 px-3 text-sm font-medium rounded-xl transition-all duration-200 ${
                    link.active
                      ? "bg-blue-600 text-white shadow"
                      : link.url
                      ? "text-gray-600 hover:bg-gray-100"
                      : "text-gray-300 cursor-not-allowed"
                  }`}
                >
                  {link.label === "&laquo; Previous"
                    ? "←"
                    : link.label === "Next &raquo;"
                    ? "→"
                    : link.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Edit Modal ──────────────────────────────────────────────────── */}
      <Modal
        visible={modals.edit}
        width="90%"
        height="auto"
        effect="fadeInUp"
        onClickAway={() => toggleModal("edit", false)}
      >
        <div className="max-w-md mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <h2 className="text-lg font-bold text-gray-900">Edit Product Message</h2>
            <button
              onClick={() => toggleModal("edit", false)}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <SvgElement type={icontypesEnum.CANCEL} />
            </button>
          </div>
          <form onSubmit={updateProduct} className="p-6 space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Custom Message
              </label>
              <textarea
                rows={4}
                defaultValue={selectedProduct?.link_info?.message}
                onChange={(e) => setMessage(e.target.value)}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none transition-all"
                placeholder="Enter a custom message for this product…"
              />
            </div>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => toggleModal("edit", false)}
                className="px-5 py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-5 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-colors"
              >
                Save Message
              </button>
            </div>
          </form>
        </div>
      </Modal>

      {/* ── Delete Modal ────────────────────────────────────────────────── */}
      <Modal
        visible={modals.delete}
        width="90%"
        height="auto"
        effect="fadeInUp"
        onClickAway={() => toggleModal("delete", false)}
      >
        <div className="max-w-sm mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <h2 className="text-lg font-bold text-red-600">Delete Product</h2>
            <button
              onClick={() => toggleModal("delete", false)}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <SvgElement type={icontypesEnum.CANCEL} />
            </button>
          </div>
          <div className="p-6">
            <div className="w-14 h-14 bg-red-100 rounded-2xl flex items-center justify-center mb-4 mx-auto">
              <FaTrash className="text-red-500" size={22} />
            </div>
            <p className="text-center text-gray-700 text-sm leading-relaxed mb-6">
              This will permanently remove the product and all its data. This action{" "}
              <strong>cannot be undone</strong>.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => toggleModal("delete", false)}
                className="flex-1 py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
              >
                Keep It
              </button>
              <button
                onClick={deleteProduct}
                disabled={loading}
                className="flex-1 py-2.5 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 rounded-xl transition-colors"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <Oval height={14} width={14} color="#fff" secondaryColor="#fca5a5" />
                    Deleting…
                  </span>
                ) : (
                  "Yes, Delete"
                )}
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {/* ── Exceed Limit Modal ──────────────────────────────────────────── */}
      <Modal
        visible={modals.exceed}
        width="90%"
        height="auto"
        effect="fadeInUp"
        onClickAway={() => toggleModal("exceed", false)}
      >
        <div className="max-w-sm mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <h2 className="text-lg font-bold text-gray-900">Upgrade Required</h2>
            <button
              onClick={() => toggleModal("exceed", false)}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <SvgElement type={icontypesEnum.CANCEL} />
            </button>
          </div>
          <div className="p-6">
            <div className="w-14 h-14 bg-amber-100 rounded-2xl flex items-center justify-center mb-4 mx-auto">
              <FaStore className="text-amber-500" size={22} />
            </div>
            <h3 className="text-center font-bold text-gray-900 mb-1">Product Limit Reached</h3>
            <p className="text-center text-gray-500 text-sm mb-6 leading-relaxed">
              You've hit your plan's product limit. Upgrade to add more products and unlock
              advanced features.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => toggleModal("exceed", false)}
                className="flex-1 py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
              >
                Later
              </button>
              <NavLink
                to="/subscription"
                className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-colors"
              >
                Upgrade <FaChevronRight size={10} />
              </NavLink>
            </div>
          </div>
        </div>
      </Modal>

      <ToastContainer
        position="bottom-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        theme="light"
        toastStyle={{ borderRadius: "12px" }}
      />
    </div>
  );
}
